<script lang="ts">
	import { Button, Badge, Heading } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { configurationStore } from '../../AppConfiguration';
	import { RoutedLink } from '../../components';
	import { listUsers, listAuditLogs } from '../../AppNetwork';
	import { onMount } from 'svelte';
	import type { User, AuditLog } from '../../AppTypes';
	import { t, t2 } from '../../i18n';

	let stats = $state({
		totalUsers: 0,
		adminUsers: 0,
		recentLogins: 0,
		auditEntries: 0
	});
	let loading = $state(true);
	let error = $state('');

	onMount(async () => {
		await loadStats();
	});

	/**
	 *
	 */
	async function loadStats() {
		loading = true;
		error = '';

		/** Fetch user and audit stats in parallel and derive dashboard counts. */
		try {
			// Load user stats and audit entries in parallel
			const [usersResult, auditResult] = await Promise.all([
				listUsers({ limit: 50 }),
				listAuditLogs({ limit: 50 })
			]);

			stats.totalUsers = usersResult.total || 0;
			stats.adminUsers = usersResult.items?.filter((u: User) => u.isAdmin).length || 0;
			stats.auditEntries = auditResult.total || 0;

			// Count recent logins (last 24 hours)
			const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
			stats.recentLogins =
				auditResult.items?.filter((e: AuditLog) => e.action === 'USER_LOGIN' && new Date(e.created) > oneDayAgo)
					.length || 0;
		} catch (err) {
			error = t2('Failed to load admin statistics');
			console.error('Admin stats error:', err);
		} finally {
			loading = false;
		}
	}
</script>

<Heading tag="h3">{$t("Administration")}</Heading>

<div class="space-y-6">
	<!-- Quick Stats -->
	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
		<AbstractCard class="p-4 max-w-none">
			<div class="flex items-center gap-3">
				<div class="rounded-lg bg-blue-100 p-3 dark:bg-blue-900/30">
					<i class="fa-solid fa-users text-blue-600 dark:text-blue-400"></i>
				</div>
				<div>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}
							<i class="fa-solid fa-spinner fa-spin text-lg"></i>
						{:else}
							{stats.totalUsers}
						{/if}
					</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">{$t("Total Users")}</p>
				</div>
			</div>
		</AbstractCard>

		<AbstractCard class="p-4 max-w-none">
			<div class="flex items-center gap-3">
				<div class="rounded-lg bg-yellow-100 p-3 dark:bg-yellow-900/30">
					<i class="fa-solid fa-shield text-yellow-600 dark:text-yellow-400"></i>
				</div>
				<div>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}
							<i class="fa-solid fa-spinner fa-spin text-lg"></i>
						{:else}
							{stats.adminUsers}
						{/if}
					</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">{$t("Administrators")}</p>
				</div>
			</div>
		</AbstractCard>

		<AbstractCard class="p-4 max-w-none">
			<div class="flex items-center gap-3">
				<div class="rounded-lg bg-green-100 p-3 dark:bg-green-900/30">
					<i class="fa-solid fa-right-to-bracket text-green-600 dark:text-green-400"></i>
				</div>
				<div>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}
							<i class="fa-solid fa-spinner fa-spin text-lg"></i>
						{:else}
							{stats.recentLogins}
						{/if}
					</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">{$t("Logins (24h)")}</p>
				</div>
			</div>
		</AbstractCard>

		<AbstractCard class="p-4 max-w-none">
			<div class="flex items-center gap-3">
				<div class="rounded-lg bg-purple-100 p-3 dark:bg-purple-900/30">
					<i class="fa-solid fa-clipboard-list text-purple-600 dark:text-purple-400"></i>
				</div>
				<div>
					<p class="text-2xl font-bold text-gray-900 dark:text-white">
						{#if loading}
							<i class="fa-solid fa-spinner fa-spin text-lg"></i>
						{:else}
							{stats.auditEntries}
						{/if}
					</p>
					<p class="text-sm text-gray-500 dark:text-gray-400">{$t("Audit Entries")}</p>
				</div>
			</div>
		</AbstractCard>
	</div>

	{#if error}
		<div class="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>
			{error}
			<Button size="xs" color="red" class="ms-4" onclick={loadStats}>{$t("Retry")}</Button>
		</div>
	{/if}

	<!-- Quick Actions -->
	<AbstractCard class="max-w-none">
		<h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
			<i class="fa-solid fa-bolt me-2"></i>
			{$t("Quick Actions")}
		</h3>

		<div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
			<RoutedLink to="/Users">
				<div
					class="rounded-lg border border-gray-200 p-4 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
				>
					<div class="mb-2 flex items-center gap-3">
						<i class="fa-solid fa-user-gear text-primary-600 dark:text-primary-400"></i>
						<span class="font-medium text-gray-900 dark:text-white">{$t("Manage Users")}</span>
					</div>
					<p class="text-sm text-gray-500 dark:text-gray-400">{$t("View, search, and manage user accounts")}</p>
				</div>
			</RoutedLink>

			<RoutedLink to="/Settings/Admin/Logs">
				<div
					class="rounded-lg border border-gray-200 p-4 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
				>
					<div class="mb-2 flex items-center gap-3">
						<i class="fa-solid fa-scroll text-primary-600 dark:text-primary-400"></i>
						<span class="font-medium text-gray-900 dark:text-white">{$t("Audit Logs")}</span>
					</div>
					<p class="text-sm text-gray-500 dark:text-gray-400">{$t("Review system activity and security events")}</p>
				</div>
			</RoutedLink>

			<button
				onclick={loadStats}
				class="rounded-lg border border-gray-200 p-4 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
			>
				<div class="mb-2 flex items-center gap-3">
					<i class="fa-solid fa-refresh text-primary-600 dark:text-primary-400"></i>
					<span class="font-medium text-gray-900 dark:text-white">{$t("Refresh Stats")}</span>
				</div>
				<p class="text-sm text-gray-500 dark:text-gray-400">{$t("Reload all administration statistics")}</p>
			</button>
		</div>
	</AbstractCard>

	<!-- System Information -->
	<AbstractCard class="max-w-none">
		<h3 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
			<i class="fa-solid fa-info-circle me-2"></i>
			{$t("System Information")}
		</h3>

		<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
			<div class="space-y-3">
				<div class="flex justify-between border-b border-gray-100 py-2 dark:border-gray-700">
					<span class="text-gray-600 dark:text-gray-400">{$t("Application")}</span>
					<span class="text-gray-900 dark:text-white">{$configurationStore?.name || 'AbstractApp'}</span>
				</div>
				<div class="flex justify-between border-b border-gray-100 py-2 dark:border-gray-700">
					<span class="text-gray-600 dark:text-gray-400">{$t("Environment")}</span>
					<Badge color="yellow">N/A</Badge>
				</div>
				<div class="flex justify-between py-2">
					<span class="text-gray-600 dark:text-gray-400">{$t("Version")}</span>
					<span class="text-gray-900 dark:text-white">{$configurationStore?.version || '1.0.0'}</span>
				</div>
			</div>

			<div class="space-y-3">
				<div class="flex justify-between border-b border-gray-100 py-2 dark:border-gray-700">
					<span class="text-gray-600 dark:text-gray-400">{$t("Auth Provider")}</span>
					<span class="text-gray-900 dark:text-white">Abstract ID</span>
				</div>
				<div class="flex justify-between border-b border-gray-100 py-2 dark:border-gray-700">
					<span class="text-gray-600 dark:text-gray-400">{$t("Database")}</span>
					<span class="text-gray-900 dark:text-white">PostgreSQL</span>
				</div>
				<div class="flex justify-between py-2">
					<span class="text-gray-600 dark:text-gray-400">{$t("Framework")}</span>
					<span class="text-gray-900 dark:text-white">Svelte 4 + Express</span>
				</div>
			</div>
		</div>
	</AbstractCard>
</div>
